import * as Dialog from '@radix-ui/react-dialog'
import { CornerDownLeft, FileSearch, Search } from '@/design-system/icons'
import { useEffect, useMemo, useState } from 'react'
import { cn, Kbd, ScanButton } from '@/design-system'
import { parseAwbs } from '@/domain/awb'
import { useT } from '@/i18n'
import { flattenPages } from '@/navigation/tree'
import { searchNav, useVisibleNav } from '@/navigation/useNavigation'
import { useCan } from '@/stores/auth'
import { useTabs } from '@/stores/tabs'

/** A shipment code rather than a page name: a long number, or a company prefix with a short number (owner, 2026-10-31: «her686 … مش بيظهر في السيرش فوق»). */
export const looksLikeShipmentCode = (awbs: string[]) => awbs.some((a) => /\d{4,}/.test(a) || /^[A-Za-z]{2,6}-?\d{3,}/.test(a))

/** Ctrl/⌘+K: jump to any permitted page, or open an AWB directly. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t, l, locale } = useT()
  const openTab = useTabs((s) => s.open)
  const tree = useVisibleNav()
  const canShipments = useCan('shipments:read')
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  // what the box searches: everything, only shipments (AWB / ref / customer mobile), or only pages
  const [mode, setMode] = useState<'all' | 'shipment' | 'pages'>('all')
  const ar = locale === 'ar'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) { setQuery(''); setMode('all') }
    setIndex(0)
  }, [open, query])

  const items = useMemo(() => {
    const list: { key: string; title: string; subtitle: string; to: string; icon?: 'awb' }[] = []
    const q = query.trim()
    if (canShipments && mode !== 'pages' && q) {
      // Arabic digits and a number without its leading 0 too (owner, 2026-10-29: «مش بيعمل سيرش بالرقم»)
      const digits = q.replace(/[٠-٩]/g, (x) => String(x.charCodeAt(0) - 0x0660)).replace(/[\s-]/g, '').replace(/^(\+?20|0020)/, '0').replace(/^1[0125]\d{8}$/, (m) => `0${m}`)
      const awbs = parseAwbs(q).awbs
      const looksCode = looksLikeShipmentCode(awbs)
      if (/^01[0125]\d{8}$/.test(digits)) {
        list.push({ key: 'mobile', title: ar ? `شحنات رقم الهاتف ${digits}` : `Shipments of mobile ${digits}`, subtitle: ar ? 'البحث برقم هاتف العميل في الاستعلام التفصيلي' : 'Customer mobile in Detail Inquiry', to: `/reports/shipment-details?mobiles=${digits}`, icon: 'awb' })
      } else if (awbs.length > 1) {
        list.push({ key: 'awbs', title: ar ? `بحث ${awbs.length} شحنة` : `Search ${awbs.length} shipments`, subtitle: awbs.slice(0, 4).join('، '), to: `/reports/shipment-details?awbs=${encodeURIComponent(awbs.join(','))}`, icon: 'awb' })
      } else if (awbs.length === 1 && (mode === 'shipment' || looksCode)) {
        list.push({ key: 'awb', title: `${ar ? 'فتح الشحنة' : 'Open shipment'} ${awbs[0]}`, subtitle: ar ? 'تفاصيل الشحنة وتتبعها' : 'Shipment details and tracking', to: `/reports/awb-tracking?awb=${encodeURIComponent(awbs[0]!)}`, icon: 'awb' })
        if (mode === 'shipment') list.push({ key: 'ref', title: ar ? `البحث بالرقم ${awbs[0]} في الاستعلام التفصيلي` : `Search ${awbs[0]} in Detail Inquiry`, subtitle: ar ? 'إذا كان رقم مرجع (Ref) أو بوليصة' : 'As a reference or AWB', to: `/reports/shipment-details?awbs=${encodeURIComponent(awbs[0]!)}`, icon: 'awb' })
      }
    }
    if (mode === 'shipment') return list
    const pages = query ? searchNav(tree, query, 12) : flattenPages(tree).slice(0, 8).map((p) => ({ ...p, score: 0 }))
    for (const r of pages) list.push({ key: r.page.id, title: l(r.page.label), subtitle: r.trail.map((g) => l(g.label)).join(' › '), to: r.page.path })
    return list
  }, [query, tree, canShipments, l, locale, t, mode, ar])

  const go = (to: string) => {
    onOpenChange(false)
    openTab(to) // its own tab (or the one already showing it); the current page stays open
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed start-1/2 top-[12vh] z-50 w-[calc(100vw-1.5rem)] max-w-xl overflow-hidden rounded-xl border border-border bg-surface-raised shadow-lg data-[state=open]:animate-slide-up ltr:-translate-x-1/2 rtl:translate-x-1/2">
          <Dialog.Title className="sr-only">{t('shell.commandHint')}</Dialog.Title>
          <Dialog.Description className="sr-only">{t('shell.commandHint')}</Dialog.Description>
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search size={16} className="text-fg-subtle" aria-hidden />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={mode === 'shipment' ? (ar ? 'رقم الشحنة أو هاتف العميل أو أكثر من بوليصة' : 'AWB, customer mobile or several AWBs') : mode === 'pages' ? (ar ? 'اسم الصفحة' : 'Page name') : t('shell.commandHint')}
              className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-fg-subtle"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') (e.preventDefault(), setIndex((i) => Math.min(i + 1, items.length - 1)))
                if (e.key === 'ArrowUp') (e.preventDefault(), setIndex((i) => Math.max(i - 1, 0)))
                if (e.key === 'Enter' && items[index]) go(items[index]!.to)
              }}
            />
            <ScanButton onScan={(c) => { const a = parseAwbs(c).awbs[0]; if (a) go(`/reports/awb-tracking?awb=${encodeURIComponent(a)}`); else setQuery(c) }} />
            <Kbd>Esc</Kbd>
          </div>
          <div className="flex gap-1 border-b border-border px-3 py-2" role="tablist">
            {([['all', ar ? 'الكل' : 'All'], ['shipment', ar ? 'شحنة' : 'Shipment'], ['pages', ar ? 'صفحات' : 'Pages']] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} className={cn('h-8 rounded-lg px-3 text-xs font-semibold transition', mode === k ? 'bg-accent text-white shadow-sm' : 'text-fg-muted hover:bg-surface-hover')}>{label}</button>
            ))}
          </div>
          <ul className="max-h-[50vh] overflow-y-auto p-1.5" role="listbox">
            {items.length === 0 && <li className="px-3 py-8 text-center text-sm text-fg-muted">{t('shell.noNavResults')}</li>}
            {items.map((it, i) => (
              <li key={it.key} role="option" aria-selected={i === index}>
                <button onMouseEnter={() => setIndex(i)} onClick={() => go(it.to)} className={cn('flex w-full items-center gap-3 rounded-md px-3 py-2 text-start', i === index && 'bg-surface-hover')}>
                  {it.icon === 'awb' ? <FileSearch size={16} className="text-brand" aria-hidden /> : <span className="size-4" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{it.title}</span>
                    <span className="block truncate text-xs text-fg-muted">{it.subtitle}</span>
                  </span>
                  {i === index && <CornerDownLeft size={14} className="text-fg-subtle" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
